import { CircleNotch } from "@phosphor-icons/react";
import { Slot, type SlotProps, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import {
  type ButtonHTMLAttributes,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
} from "react";

import { cn } from "common/helpers/utils";
import useWhitelabelCustomization from "common/hooks/useWhitelabelCustomization";

import type React from "react";

const defaultButtonClasses = [
  "transition-[background] duration-300 ease-in-out cursor-pointer rounded-full",
  "focus-visible:outline-none",
  "disabled:cursor-not-allowed disabled:bg-black-200 disabled:text-black-400 disabled:border-black-200",
];

const linkButtonClasses = [
  "transition-[color,border-color] duration-300 ease-in-out cursor-pointer py-0",
  "disabled:cursor-not-allowed disabled:text-black-400",
];

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center text-nowrap",
  {
    variants: {
      variant: {
        // Default
        "default-black": "bg-black-950 text-white hover:bg-black-700",
        "default-purple": "bg-purple-500 text-white hover:bg-purple-400",

        // Secondary
        "secondary-black": "bg-black-100 text-black-950 hover:bg-black-200",
        "secondary-purple": "text-purple-600",

        // Outline
        "outline-black":
          "border border-black-200 bg-white text-black-950 hover:bg-black-100",
        "outline-danger":
          "border border-red-200 bg-white text-red-500 hover:bg-red-50",

        // Ghost
        "ghost-black": "text-black-950 hover:bg-black-100",
        "ghost-purple": "text-purple-600 hover:bg-purple-100",
        "ghost-danger": "text-red-500 hover:bg-red-200",

        // Link - No border radius, different spacing, different disabled state
        "link-black": "text-black-950 hover:text-black-700",
        "link-purple": "text-purple-600 hover:text-purple-400",
        "link-danger": "text-black-500 hover:text-red-500",

        // Gradient Button (AI)
        gradient:
          "text-white transition-opacity bg-blackPurple disabled:bg-none hover:opacity-80 disabled:hover:opacity-100",
      },
      size: {
        // XS Variant is used for Icon Buttons only
        xs: "size-6 [&>svg]:size-4",
        sm: "h-8 min-w-8 gap-1 px-4 py-2 text-button-xs [&>svg]:size-4",
        md: "h-10 min-w-10 gap-2 px-5 py-2.5 text-button-sm [&>svg]:size-5",
        lg: "h-11 min-w-11 gap-2 px-6 py-3 text-button-md [&>svg]:size-6",
      },
      intent: {
        default: defaultButtonClasses,
        iconOnly: [...defaultButtonClasses, "p-0 rounded-full"],
        labelIcon: "pointer-events-none p-0 rounded-full",
        link: linkButtonClasses,
      },
    },
    compoundVariants: [
      {
        variant: "secondary-purple",
        intent: ["iconOnly", "labelIcon"],
        class: "bg-purple-100 hover:bg-purple-200",
      },
      {
        variant: "secondary-purple",
        intent: "default",
        class: "bg-purple-50 hover:bg-purple-100",
      },
      {
        variant: ["link-black", "link-purple", "link-danger"],
        size: "sm",
        class: "h-auto px-1 gap-1 text-xs-bold",
      },
      {
        variant: ["link-black", "link-purple", "link-danger"],
        size: "md",
        class: "h-auto px-2.5 gap-1.5 text-sm-bold",
      },
      {
        variant: ["link-black", "link-purple", "link-danger"],
        size: "lg",
        class: "h-auto px-3 gap-2 text-md-bold",
      },
    ],
  },
);

type IconPosition = "left" | "right" | "both";
const iconStyleOptions: Record<
  IconPosition,
  { sm: string; md: string; lg: string }
> = {
  both: {
    sm: "px-2",
    md: "px-2.5",
    lg: "px-3",
  },
  left: {
    sm: "[&_svg]:-ml-2",
    md: "[&_svg]:-ml-2.5",
    lg: "[&_svg]:-ml-3",
  },
  right: {
    sm: "[&_svg]:-mr-2",
    md: "[&_svg]:-mr-2.5",
    lg: "[&_svg]:-mr-3",
  },
};

type ButtonVariantType = VariantProps<typeof buttonVariants>["variant"];

// Extract intent type and exclude 'link' from public API
type ButtonIntentType = Exclude<
  VariantProps<typeof buttonVariants>["intent"],
  "link"
>;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<VariantProps<typeof buttonVariants>, "intent"> & {
    intent?: ButtonIntentType;
    asChild?: boolean;
    isLoading?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
  };

function Button(
  {
    className,
    variant = "default-black",
    size = "md",
    intent = "default",
    children,
    isLoading = false,
    leftIcon = null,
    rightIcon = null,
    asChild = false,
    disabled,
    ...props
  }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const { whitelabelCustomization } = useWhitelabelCustomization();

  const loadingComponent = <CircleNotch className="animate-spin" />;

  // Icon buttons don't have link variants
  const isLinkVariant = variant?.startsWith("link-");

  let Comp:
    | React.ForwardRefExoticComponent<
        SlotProps & React.RefAttributes<HTMLElement>
      >
    | "button"
    | "span" = "button";

  if (intent === "labelIcon") {
    Comp = "span";
  } else if (asChild) {
    Comp = Slot;
  }

  let iconPosition: IconPosition;
  if (leftIcon && !rightIcon) {
    iconPosition = "left";
  } else if (rightIcon && !leftIcon) {
    iconPosition = "right";
  } else if (rightIcon && leftIcon) {
    iconPosition = "both";
  }

  let whitelabelClass: string;
  if (whitelabelCustomization) {
    if (variant === "default-black" && whitelabelCustomization.primary_color) {
      whitelabelClass =
        "bg-[--whitelabel-color] hover:bg-[hsl(from_var(--whitelabel-color)_h_s_l/0.6)] text-[oklch(from_var(--whitelabel-color)_clamp(0,(l/0.7-1)*-infinity,1)_0_h/1)]";
    } else if (variant === "secondary-purple") {
      variant = "secondary-black";
    }
  }

  const iconStyle =
    iconPosition && size !== "xs" ? iconStyleOptions[iconPosition][size] : "";

  return (
    <Comp
      className={cn(
        buttonVariants({
          variant,
          size,
          intent: isLinkVariant ? "link" : intent,
          className: clsx(className, whitelabelClass),
        }),
        iconStyle,
      )}
      type="button"
      ref={ref}
      {...props}
      disabled={isLoading || disabled}
    >
      {isLoading && leftIcon ? loadingComponent : leftIcon}

      {intent === "iconOnly" && isLoading ? (
        loadingComponent
      ) : (
        <Slottable>{children}</Slottable>
      )}
      {isLoading &&
      (rightIcon || (!rightIcon && !leftIcon && intent !== "iconOnly"))
        ? loadingComponent
        : rightIcon}
    </Comp>
  );
}

Button.displayName = "Button";

const ButtonWithRef = forwardRef(Button);

export { ButtonWithRef as Button, type ButtonVariantType };
